import { Controller }  from "stimulus"
import { getRequest, pathname } from "@react/helpers/fetch"

export default class extends Controller {

  static targets = [
    "paymentDelay",
    "endOfMonth",
    "clientCode",
    "clientName",
    "clientNameAddition",
    "clientNameAddition2",
    "clientAddress",
    "reminderCheckbox",
    "onlinePaymentCheckbox",
    "billingCategory"
  ]

  updateClient() {
    if (this.hasPaymentDelayTarget) {
      const paymentDelay = this._readDataElement().client.payment_delay
      if (paymentDelay) this.paymentDelayTarget.value = paymentDelay
    }

    if (this.hasEndOfMonthTarget) {
      const endOfMonth = this._readDataElement().client.end_of_month
      if (endOfMonth) this.endOfMonthTarget.value = endOfMonth
    }

    if (this.hasClientNameTarget) this.updateBillingData()

    if (this.hasReminderCheckboxTarget) this.updateReminderCheckbox()

    if (this.hasOnlinePaymentCheckboxTarget) this.updateOnlinePaymentCheckbox()

    if (this.hasBillingCategoryTarget) this.resetBillingCategories()

    this.resetLineTemplates()
  }

  updateSite() {
    const siteVatRateId = this._readDataElement().site.vat_rate?.id
    if (siteVatRateId) this._setVatRates(siteVatRateId)
  }

  updateSupplier(e) {
    const dataElement = document.getElementById("suppliers")
    const props       = JSON.parse(dataElement.dataset.reactProps)
    const supplier    = props.options.find(supplier => supplier.id == e.target.value)
    if (supplier && supplier.vatRateId) this._setVatRates(supplier.vatRateId)
  }

  updateBillingData() {
    const billing_details = this._readDataElement().client.billing_details
    if (!billing_details) return

    if (this.hasClientCodeTarget) {
      const clientCode = billing_details.client_code
      this.clientCodeTarget.value = clientCode
    }

    if (this.hasClientNameTarget) {
      const clientName = billing_details.client_name
      this.clientNameTarget.value = clientName
    }

    if (this.hasClientNameAdditionTarget) {
      const clientNameAddition = billing_details.client_name_addition
      this.clientNameAdditionTarget.value = clientNameAddition
    }

    if (this.hasClientNameAddition2Target) {
      const clientNameAddition2 = billing_details.client_name_addition2
      this.clientNameAddition2Target.value = clientNameAddition2
    }

    if (this.hasClientAddressTarget) {
      const clientAddress = billing_details.client_address
      this.clientAddressTarget.value = clientAddress
    }
  }

  updateReminderCheckbox() {
    const invoiceRemindersEnabled = this._readDataElement().client.invoice_reminders_enabled_by_default
    this.reminderCheckboxTarget.checked = invoiceRemindersEnabled;
  }

  updateOnlinePaymentCheckbox() {
    const onlinePaymentEnabledByDefault = this._readDataElement().client.online_payment_enabled_by_default
    this.onlinePaymentCheckboxTarget.checked = onlinePaymentEnabledByDefault;
  }

  resetBillingCategories() {
    const data               = this._readDataElement()
    const clientId           = data?.client?.id
    const billingCategories  = data?.client?.billing_categories || []

    this.billingCategoryTargets.forEach(target => {
      target.value = ''
      target.dispatchEvent(new Event('change'))

      const reactContainer = target.closest('[data-react-class]')
      if (reactContainer && reactContainer.dataset.reactProps) {
        this._writeBillingCategories(reactContainer, clientId, billingCategories)
        reloadReact(reactContainer.parentElement)
      }
    })
  }

  resetLineTemplates() {
    const data               = this._readDataElement()
    const clientId           = data?.client?.id
    const billingCategories  = data?.client?.billing_categories || []

    document.querySelectorAll("[data-association-insertion-template]").forEach(link => {
      const holder     = document.createElement("div")
      holder.innerHTML = this._readLineTemplate(link)

      const container = this._billingCategoryContainer(holder)
      if (!container) return

      this._writeBillingCategories(container, clientId, billingCategories)
      this._writeLineTemplate(link, holder.innerHTML)
    })
  }

  addLine() {
    setTimeout(() => {
      let lines = document.querySelectorAll(".nested-fields")
      lines = Array.from(lines).filter(row => row.style.display != "none")
      lines.forEach((line, index) => {
        const quill = line.querySelector(".ql-editor")
        const order = line.querySelector("input[name*='[order]']")
        if (order)    order.value = index + 1
        if (!quill) reloadReact(line)
        // Update global client discount
        const lastLineIndex = lines.length - 1
        if (index == lastLineIndex) {
          const data           = this._readDataElement()
          const clientDiscount = data?.client?.discount
          const discountField  = line.querySelector("input[name*='[discount]']")
          if (clientDiscount && discountField) discountField.value = clientDiscount

          this._focusProductSearch(line)
        }
      })
    }, 300)
  }

  updateContract() {
    const data    = this._readDataElement()
    const contract = data?.mbc_contract
    const lines   = Array.from(document.querySelectorAll(".nested-fields"))
      .filter(line => line.style.display !== "none")

    if (contract?.prices) {
      lines.forEach(line => {
        const productIdInput = line.querySelector("input[name*='[product_id]']")
        if (!productIdInput || !productIdInput.value) return

        const productId = parseInt(productIdInput.value)
        const mbcPrice  = contract.prices[productId]
        if (mbcPrice === undefined) return

        const priceInput = line.querySelector("input[name*='[unit_price]']")
        if (!priceInput) return

        priceInput.value = mbcPrice
        priceInput.dispatchEvent(new Event("keyup"))
      })
    } else if (data?.mounted) {
      // Contract removed: restore regular prices via API
      const client = document.querySelector("input[name*='[client_id]']")

      lines.forEach(line => {
        const productIdInput = line.querySelector("input[name*='[product_id]']")
        if (!productIdInput || !productIdInput.value) return

        const path = line.getAttribute("data-financial--product-path")
        if (!path) return

        const params = { product: productIdInput.value }
        if (client && client.value) params.client = client.value

        const url = pathname({ path, format: "json", params })
        getRequest(url).then(response => {
          const priceInput = line.querySelector("input[name*='[unit_price]']")
          if (!priceInput) return

          priceInput.value = response.product.sale_price
          priceInput.dispatchEvent(new Event("keyup"))
        })
      })
    }
  }

  updateCurrency(e) {
    const new_currency = e.target.value
    const currencyElement = document.querySelector('[data-financial--total-currency]')
    currencyElement.setAttribute('data-financial--total-currency', new_currency)
  }


  // private

  _billingCategoryContainer(scope) {
    return Array.from(scope.querySelectorAll("[data-react-class]")).find(element => {
      const target = JSON.parse(element.dataset.reactProps || "{}").data?.target
      return target?.includes("financial--assets.billingCategory")
    })
  }

  _readLineTemplate(link) {
    const cached = window.jQuery?.(link).data("association-insertion-template")
    return cached || link.dataset.associationInsertionTemplate
  }

  _writeLineTemplate(link, template) {
    link.dataset.associationInsertionTemplate = template
    window.jQuery?.(link).data("association-insertion-template", template)
  }

  _writeBillingCategories(container, clientId, billingCategories) {
    const props   = JSON.parse(container.dataset.reactProps)
    props.options = billingCategories
    props.value   = null
    props.prompt  = clientId && billingCategories.length === 0
      ? I18n.t("client.no_billing_categories")
      : I18n.t("billing_categories.select")
    container.dataset.reactProps = JSON.stringify(props)
  }

  _focusProductSearch(line) {
    if (line.getAttribute("data-financial--product-purchase") === "true") return

    setTimeout(() => {
      const container   = line.querySelector('[data-target~="financial--product.container"]')
      const searchInput = container?.querySelector(".autocomplete__input")
      if (searchInput && !searchInput.disabled) searchInput.focus()
    }, 150)
  }

  _setVatRates(vatRateId) {
    const vatRates = Array.from(document.querySelectorAll("select[name*='[vat_rate_id]']"))
    vatRates.forEach(vatRate => {
      vatRate.value = vatRateId
      vatRate.dispatchEvent(new Event("change"))
    })
  }

  _readDataElement() {
    const dataElement = document.getElementById("asset-print")
    if (!dataElement) return

    return JSON.parse(dataElement.innerText)
  }
}
